import { useContext, useEffect, useState } from 'react';
import placeholderLogo from '../../../assets/Image Placeholder.jpg';
import { DataProvider } from '../../../store/ContextProvider';
import Loading from '../../../pages/Loading';
import { compressImage } from '../../../utils/compressImage';
import { renderImgSrc } from '../../../utils/renderImgSrc';

export default function UpdateProfile({ userData, setUserData, setIsReadOnly }) {
    const { BeURL, showAlert, currentUser } = useContext(DataProvider);
    const COMPANY_SIZES = ['1-10', '11-50', '51-200', '201-500', '501-1000', '1001+'];
    const MAX_SIZE = 20 * 1024 * 1024; // 20 MB
    const ACCEPTED_TYPES = /^image\/(png|jpe?g|gif|webp|bmp|svg\+xml)$/i;
    const [form, setForm] = useState({
        companyName: '',
        companyWebsite: '',
        industry: '',
        companySize: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        country: '',
        zipCode: '',
        description: ''
    });
    const [logoFile, setLogoFile] = useState(null);
    const [previewLogo, setPreviewLogo] = useState(placeholderLogo);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false)
    const [ImgLoading, setImgLoading] = useState(false)

    // init
    useEffect(() => {
        if (!userData) return;
        setForm({
            userId: userData.user?._id || currentUser._id || "",
            companyName: userData.companyName || 'SAN Engineering solutions',
            companyWebsite: userData.companyWebsite || 'https://www.sanengineeringsolutions.com/',
            industry: userData.industry || 'HR',
            companySize: userData.companySize || '51-200',
            addressLine1: userData.headquarters?.addressLine1 || 'SAN',
            addressLine2: userData.headquarters?.addressLine2 || '',
            city: userData.headquarters?.city || 'Perundurai',
            state: userData.headquarters?.state || 'Tamil Nadu',
            country: userData.headquarters?.country || 'India',
            zipCode: userData.headquarters?.zipCode || '638052',
            description: userData.description || 
            `SAN Engineering Solutions is an integrated Engineering, Technology, Training and Staffing solutions provider. We offer Services and Solutions that are simultaneously affordable, sustainable and of high quality with cost-conscious and eco-aware product development. Our service offerings include New Product Development, Product Improvement, Reverse Engineering, Cost Reduction, Legacy Conversions and Engineering Process Support using cutting-edge CAD / CAM / CAE technology in various domains, our explicit expertise lies within the Automotive, Commercial Vehicles, Farm Equipment, Aerospace and Construction & Industrial Equipment.
            `
        });
        if (userData.companyLogo) {
            const tempUrl = renderImgSrc(userData.companyLogo, userData.companyName)
            setPreviewLogo(tempUrl);
        }
    }, [userData, BeURL]);

    // validation
    useEffect(() => {
        const e = {};
        if (!form.companyName.trim()) e.companyName = 'Required';
        if (!form.companyWebsite.trim() ||
            !/^https?:\/\/\S+\.\S+/.test(form.companyWebsite))
            e.companyWebsite = 'Valid URL required';
        if (!form.industry.trim()) e.industry = 'Required';
        if (!COMPANY_SIZES.includes(form.companySize))
            e.companySize = 'Required';
        if (!form.addressLine1.trim()) e.addressLine1 = 'Required';
        if (!form.city.trim()) e.city = 'Required';
        if (!form.state.trim()) e.state = 'Required';
        if (!form.country.trim()) e.country = 'Required';
        if (!form.zipCode.trim() || !/^\d{3,10}$/.test(form.zipCode))
            e.zipCode = '3–10 digit zip required';
        setErrors(e);
    }, [form]);

    const manBadge = () => {
      return <span className='text-red-600 text-lg font-medium'> *</span>
    }

    const handleChange = field => e =>
        setForm(f => ({ ...f, [field]: e.target.value }));

    const fileToDataUrl = (f) =>
        new Promise((resolve, reject) => {
            try {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = (e) => reject(e);
                reader.readAsDataURL(f);
            } catch (e) {
                reject(e);
            }
        });

    const validateImageSrc = (src) =>
        new Promise((resolve) => {
            if (!src) return resolve(false);
            const img = new Image();
            img.onload = () => resolve(true);
            img.onerror = () => resolve(false);
            img.src = src;
        });

    const handleLogoChange = async (e) => {
        const rawFile = e.target.files[0];
        if (!rawFile) return;

        if (!rawFile.type || !ACCEPTED_TYPES.test(rawFile.type)) {
            showAlert("Please upload a valid image (PNG, JPG, JPEG, GIF, WEBP, BMP, or SVG).");
            return;
        }
        if (rawFile.size > MAX_SIZE) {
            showAlert("File is too large. Maximum allowed size is 20MB.");
            return;
        }

        try {
            setImgLoading(true);
            let compressed = await compressImage(rawFile)
            if (!compressed) compressed = rawFile;
            const dataUrl = await fileToDataUrl(compressed);
            const ok = await validateImageSrc(dataUrl);
            if (!ok) {
                showAlert("Uploaded image could not be loaded. Try another file.");
                setImgLoading(false);
                return;
            }

            setLogoFile(compressed)
            setPreviewLogo(dataUrl)
            setImgLoading(false);
        } catch (err) {
            console.error("Error processing file:", err);
            showAlert("Error while processing image.");
            setImgLoading(false);
        }

        // setLogoFile(file);
        // setPreviewLogo(URL.createObjectURL(file));
    };

    const handleUpdation = async () => {
        if (Object.keys(errors).length) {
            showAlert('Fix errors first');
            return;
        }
        const data = new FormData();
        data.append('userId', userData.user?._id || "")
        data.append('companyName', form.companyName);
        data.append('companyWebsite', form.companyWebsite);
        data.append('industry', form.industry);
        data.append('companySize', form.companySize);
        data.append('addressLine1', form.addressLine1);
        data.append('addressLine2', form.addressLine2);
        data.append('city', form.city);
        data.append('state', form.state);
        data.append('country', form.country);
        data.append('zipCode', form.zipCode);
        data.append('description', form.description);
        if (logoFile) data.append('companyLogo', logoFile);

        try {
            setLoading(true)
            const res = await fetch(`${BeURL}/update-employer`, {
                method: 'POST',
                credentials: 'include',
                body: data
            });
            const result = await res.json();
            showAlert(result.message);
            if (result.success) {
                setUserData(prev => ({ ...prev, ...result.employer }));
                setIsReadOnly(true);
            }

            setLoading(false)
        } catch (err) {
            console.error(err);
            showAlert('Server error.');
            setLoading(false)
        }
    };

    const renderErr = field =>
        errors[field] && <p className="text-red-600 text-sm">{errors[field]}</p>;

    if (!userData) return <Loading/>

    return (
        <div className="p-4 bg-white rounded-lg shadow space-y-6">
            {/* Logo Upload */}
            <div className="flex items-center space-x-4">
                {!ImgLoading && <label className="block">
                    <img
                        src={previewLogo}
                        alt="Company Logo"
                        className="w-24 h-24 object-cover rounded border"
                    />
                    <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoChange}
                        className="hidden"
                        readOnly={ImgLoading}
                    />
                </label>}
                <span className="text-sm text-gray-500">{ImgLoading ? 'Processing image...' : 'Click the logo to upload'}</span>
            </div>

            {/* Company Details */}
            <div>
                <label className="block font-medium">Company Name{manBadge()}</label>
                <input
                    type="text"
                    value={form.companyName}
                    onChange={handleChange('companyName')}
                    className="w-full border rounded p-2"
                />
                {renderErr('companyName')}
            </div>

            <div>
                <label className="block font-medium">Website{manBadge()}</label>
                <input
                    type="url"
                    value={form.companyWebsite}
                    onChange={handleChange('companyWebsite')}
                    className="w-full border rounded p-2"
                />
                {renderErr('companyWebsite')}
            </div>

            <div>
                <label className="block font-medium">Industry{manBadge()}</label>
                <input
                    type="text"
                    value={form.industry}
                    onChange={handleChange('industry')}
                    className="w-full border rounded p-2"
                />
                {renderErr('industry')}
            </div>

            <div>
                <label className="block font-medium">Company Size{manBadge()}</label>
                <select
                    value={form.companySize}
                    onChange={handleChange('companySize')}
                    className="w-full border rounded p-2"
                >
                    <option value="" disabled>Select size</option>
                    {COMPANY_SIZES.map(sz => (
                        <option key={sz} value={sz}>{sz}</option>
                    ))}
                </select>
                {renderErr('companySize')}
            </div>

            {/* Headquarters */}
            <h5 className="font-semibold">Headquarters</h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {['addressLine1', 'addressLine2', 'city', 'state', 'country', 'zipCode'].map((f, idx) => (
                    <div key={f}>
                        <label className="block font-medium">
                            {f === 'addressLine1' ? 'Address Line 1' :
                                f === 'addressLine2' ? 'Address Line 2' :
                                    f.charAt(0).toUpperCase() + f.slice(1)
                            }
                            { f !== "addressLine2" && manBadge() }
                        </label>
                        <input
                            type="text"
                            value={form[f]}
                            onChange={handleChange(f)}
                            className="w-full border rounded p-2"
                        />
                        {f !== 'addressLine2' && renderErr(f)}
                    </div>
                ))}
            </div>

            {/* Description */}
            <div>
                <label className="block font-medium">About Company</label>
                <textarea
                    value={form.description}
                    onChange={handleChange('description')}
                    rows={4}
                    className="w-full border rounded p-2"
                />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-4">
                <button
                    className="px-4 py-2 border rounded text-red-600"
                    onClick={() => setIsReadOnly(true)}
                >
                    Cancel
                </button>
                <button
                    className="px-4 py-2 bg-primary-500 text-white rounded"
                    onClick={handleUpdation}
                    disabled={loading}
                >
                    {loading ? 'Updating...' : 'Update'}
                </button>
            </div>
        </div>
    );
}
